import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Star, PenLine, X, Send, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Stars({ value, size = "w-4 h-4" }) {
  return (
    <div className="flex items-center gap-0.5" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= Math.round(value) ? "text-orange-400 fill-orange-400" : "text-slate-600"}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

const DEFAULT_FORM = { author: "", role: "", location: "", rating: 5, text: "" };

export function ReviewsSection() {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  const fetchReviews = () => {
    axios.get(`${API}/reviews`).then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => { fetchReviews(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || !form.text.trim()) {
      toast.error("Please add your name and review");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you! Your review was submitted and will appear once approved.");
      setForm(DEFAULT_FORM);
      setModalOpen(false);
    } catch {
      toast.error("Could not submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <span className="overline">Client Reviews</span>
              <div className="section-divider-left mt-3" />
              <h2
                className="text-white mt-4"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: "clamp(2rem, 3vw, 2.8rem)", textTransform: "uppercase", lineHeight: 1.1 }}
              >
                What Our Buyers Say
              </h2>
              {data.count > 0 && (
                <div className="flex items-center gap-3 mt-4" data-testid="reviews-aggregate">
                  <span className="text-orange-400 font-black text-3xl" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {data.average.toFixed(1)}
                  </span>
                  <div>
                    <Stars value={data.average} />
                    <div className="text-slate-500 text-xs mt-1" style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.05em" }}>
                      {data.count} VERIFIED REVIEW{data.count !== 1 ? "S" : ""}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setModalOpen(true)}
              data-testid="write-review-btn"
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.4)]"
            >
              <PenLine className="w-4 h-4" /> Write a Review
            </button>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.reviews.map((r, i) => (
            <Reveal key={r.id} delay={(i % 3) * 0.1}>
              <div
                className="bg-[#1E293B] border border-white/[0.08] p-7 hover:border-orange-500/40 transition-all duration-300 h-full flex flex-col"
                data-testid={`review-card-${r.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <Stars value={r.rating} />
                  <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                  </span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed mb-6 flex-1">"{r.text}"</p>
                <div className="flex items-center gap-3 border-t border-white/[0.06] pt-5">
                  <div
                    className="w-10 h-10 bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-orange-400 font-bold text-sm"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {r.author.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">{r.author}</div>
                    <div className="text-slate-500 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.05em" }}>
                      {[r.role, r.location].filter(Boolean).join(" · ") || "Verified Buyer"}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Submit Review Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalOpen(false)}
            data-testid="review-modal"
          >
            <motion.div
              className="bg-[#1E293B] border border-white/10 w-full max-w-lg p-6 md:p-8 relative"
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setModalOpen(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
                data-testid="review-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <h3
                className="text-white font-bold text-2xl mb-1 uppercase"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Share Your Experience
              </h3>
              <p className="text-slate-400 text-sm mb-6">Your review helps other industrialists choose with confidence.</p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Your Rating</label>
                  <div className="flex items-center gap-1" onMouseLeave={() => setHoverRating(0)}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        type="button"
                        key={n}
                        onMouseEnter={() => setHoverRating(n)}
                        onClick={() => setForm({ ...form, rating: n })}
                        data-testid={`review-rating-${n}`}
                        className="p-0.5"
                      >
                        <Star
                          className={`w-7 h-7 transition-colors ${
                            n <= (hoverRating || form.rating) ? "text-orange-400 fill-orange-400" : "text-slate-600"
                          }`}
                          strokeWidth={1.5}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Full Name *</label>
                    <input
                      value={form.author}
                      onChange={(e) => setForm({ ...form, author: e.target.value })}
                      placeholder="Your name"
                      required
                      className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                      data-testid="review-author-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Role / Company</label>
                    <input
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      placeholder="e.g. Factory Owner"
                      className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                      data-testid="review-role-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">City / Location</label>
                  <input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Kotdwar"
                    className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                    data-testid="review-location-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Your Review *</label>
                  <textarea
                    value={form.text}
                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                    placeholder="Tell us about your experience buying an industrial plot with us..."
                    required
                    rows={4}
                    className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500 resize-none"
                    data-testid="review-text-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] disabled:opacity-60"
                  data-testid="review-submit-btn"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit Review</>}
                </button>
                <p className="text-center text-xs text-slate-500">Reviews are published after a quick verification.</p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
